import { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"

import { supabase } from "@/lib/supabase"

import { useSessions } from "./useSessions"

type RecurrencePattern = "none" | "daily" | "weekly" | "monthly"

export interface VehicleEvent {
  id: string
  title: string
  jobPostings: string[]
  date: Date
  timeSlot: string
  startTime: string
  endTime: string
  location: string
  capacity: number
  registered: number
  status: "Open" | "Closed" | "Deleted"
}

export interface NewVehicleEvent {
  title: string
  selectedJobPostings: string[]
  date: Date
  startTime: string
  endTime: string
  locationKey: string
  capacity: number
  recurrence: RecurrencePattern
  recurrenceEndDate: Date | null
  status?: string
}

interface UseVehicleEventsProps {
  jobPostings?: { value: string; label: string }[]
  locations?: { value: string; label: string }[]
  filters?: {
    depot_id?: string
    status?: "Open" | "Closed" | "Deleted"
    date_from?: string
    date_to?: string
    search?: string
  }
}

export const useVehicleEvents = ({ locations, filters = {} }: UseVehicleEventsProps) => {
  // Initialize queryClient for cache invalidation
  const queryClient = useQueryClient()

  // Initialize with default form values
  const [newEvent, setNewEvent] = useState<NewVehicleEvent>({
    title: "",
    date: new Date(),
    selectedJobPostings: [],
    startTime: "09:00",
    endTime: "11:00",
    locationKey: "",
    capacity: 5,
    recurrence: "none",
    recurrenceEndDate: null,
  })

  // Use the sessions hook with a type filter for vehicle
  const { sessions, fetchNextPage, hasNextPage, isFetchingNextPage, status, error, createSession } =
    useSessions({
      filters: {
        ...filters,
        type: "vehicle_collection", // Always filter for vehicle type sessions
      },
    })

  // Handle adding/removing job postings selection
  const toggleJobPosting = (value: string) => {
    setNewEvent((prev) => {
      const currentPostings = [...prev.selectedJobPostings]
      const index = currentPostings.indexOf(value)

      if (index === -1) {
        // Add the job posting
        return { ...prev, selectedJobPostings: [...currentPostings, value] }
      } else {
        // Remove the job posting
        currentPostings.splice(index, 1)
        return { ...prev, selectedJobPostings: currentPostings }
      }
    })
  }

  // Map sessions to VehicleEvent format
  const events: VehicleEvent[] = sessions.map((session) => {
    return {
      id: session.id,
      title: session.title || "Vehicle Check",
      jobPostings: session.jobs || [],
      date: new Date(session.date),
      timeSlot: `${session.start_time} - ${session.end_time}`,
      startTime: session.start_time || "",
      endTime: session.end_time || "",
      location:
        locations.find((loc) => loc.value === session.depot_id)?.label || session.depot_id || "",
      capacity: session.capacity || 0,
      registered: session.registered,
      status: (session.status as "Open" | "Closed" | "Deleted") || "Open",
    }
  })

  const handleCreateEvent = () => {
    if (!newEvent.locationKey || !newEvent.title) return

    // Get the formatted date as ISO string (YYYY-MM-DD)
    const formattedDate = format(newEvent.date, "yyyy-MM-dd")

    // If recurrence is none, create a single session
    if (newEvent.recurrence === "none") {
      // Create the session
      createSession.mutate({
        title: newEvent.title,
        date: formattedDate,
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        depot_id: newEvent.locationKey,
        capacity: newEvent.capacity,
        jobs: newEvent.selectedJobPostings || [], // Store job IDs/values
        status: "Open",
        type: "vehicle_collection", // Set the type to vehicle
      })
    } else {
      // Create multiple sessions based on recurrence pattern
      const sessions = []
      let currentDate = new Date(newEvent.date)
      const endDate = newEvent.recurrenceEndDate || new Date(currentDate)

      // Always create at least one session for the initial date
      sessions.push({
        date: format(currentDate, "yyyy-MM-dd"),
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        depot_id: newEvent.locationKey,
        capacity: newEvent.capacity,
        jobs: newEvent.selectedJobPostings,
        status: "Open",
        type: "vehicle_collection",
        title: newEvent.title,
      })

      // Advance to the next date based on recurrence pattern
      if (newEvent.recurrence === "daily") {
        currentDate.setDate(currentDate.getDate() + 1)
      } else if (newEvent.recurrence === "weekly") {
        currentDate.setDate(currentDate.getDate() + 7)
      } else if (newEvent.recurrence === "monthly") {
        currentDate.setMonth(currentDate.getMonth() + 1)
      } else {
        // If recurrence is "none", we don't need to create more sessions
        currentDate = new Date(endDate.getTime() + 1) // Set currentDate past endDate to exit loop
      }

      // Generate dates based on recurrence pattern until end date
      while (currentDate <= endDate) {
        // Add the session with the current date
        sessions.push({
          date: format(currentDate, "yyyy-MM-dd"),
          start_time: newEvent.startTime,
          end_time: newEvent.endTime,
          depot_id: newEvent.locationKey,
          capacity: newEvent.capacity,
          jobs: newEvent.selectedJobPostings,
          status: "Open",
          type: "vehicle_collection",
          title: newEvent.title,
        })

        // Advance to next occurrence based on pattern
        if (newEvent.recurrence === "daily") {
          currentDate.setDate(currentDate.getDate() + 1)
        } else if (newEvent.recurrence === "weekly") {
          currentDate.setDate(currentDate.getDate() + 7)
        } else if (newEvent.recurrence === "monthly") {
          currentDate.setMonth(currentDate.getMonth() + 1)
        }
      }

      // Create all sessions
      for (const session of sessions) {
        createSession.mutate(session)
      }
    }

    // Reset the form
    setNewEvent({
      title: "",
      date: new Date(),
      startTime: "09:00",
      endTime: "11:00",
      locationKey: "",
      capacity: 5,
      recurrence: "none",
      recurrenceEndDate: null,
      selectedJobPostings: [],
    })
  }

  // Handle toggling event status
  const handleToggleStatus = async (eventId: string, currentStatus: string) => {
    try {
      // Determine the new status
      const newStatus = currentStatus === "Open" ? "Closed" : "Open"

      // Update the session status in the database
      const { error } = await supabase
        .from("sessions")
        .update({ status: newStatus })
        .eq("id", eventId)

      if (error) {
        console.error("Error updating session status:", error)
        return
      }

      // Refetch the sessions data
      await queryClient.invalidateQueries({
        queryKey: ["sessions"],
      })
    } catch (error) {
      console.error("Error in handleToggleStatus:", error)
    }
  }

  return {
    events,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    newEvent,
    setNewEvent,
    toggleJobPosting,
    handleCreateEvent,
    handleToggleStatus,
    // Export mutation state for UI feedback
    createSession,
  }
}
